import { fullDefinition } from '../reactions/php-row-rules';
import { readItemField } from '../compile/item-field-reader';
import { projectAttackStatuses, type AttackStatusScene } from './attack-status-projection';
import { statusEndFields } from '../transport/applied-status-rows';
import type { AbilityUseVerdict } from './ability-use';
import { statusRows, statusItemId } from '../statuses/expiry';
import { encounterCreatureField } from '../../shared/array-types';
import { isPilotingStatus, projectPilotedAbilityDiff } from '../compile/piloted-ability-diff';
import { mergeAttackStatuses } from './attack-status-projection';
import { pilotedStatusPeers } from '../compile/piloted-object';
type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const statusId = (row: object): string => upper(readItemField(row as Row, 'status_id'));
/** Best-effort preload of NPC definitions. Status projection refuses only when a changed defense needs a missing definition. */
export async function hydrateStatusCreatureDefinitions(roster: Iterable<Row>, definition: (id: string) => Row | null,
    fetch: (id: string) => Promise<unknown>): Promise<void> {
    const ids = new Set<string>();
    for (const member of roster) if (encounterCreatureField(member.encounter_creatures, 'item_type') === 'creature') {
        const id = upper(encounterCreatureField(member.encounter_creatures, 'item_id'));
        if (id) ids.add(id);
    }
    const held = (id: string): boolean => {
        const def = fullDefinition(definition(id), 'any');
        return !!def && !def.encounter_creatures;
    };
    await Promise.all([...ids].filter(id => !held(id)).map(id => fetch(id).catch(() => undefined)));
}
/** Finish derived status fields on the acting page before its result is encoded.
 * Reuse the native status projector, including roots, grants and defenses. */
export function projectAbilityStatuses(verdict: Pick<Extract<AbilityUseVerdict, { kind: 'resolved' }>, 'statusesAdded' | 'statusesRemoved' | 'statusStacks'>, roster: ReadonlyMap<string, Row>,
    definition: (id: string) => Row | null, scene?: AttackStatusScene, encounter?: Row): string[] {
    const failures: string[] = [];
    const addedPeers = verdict.statusesAdded.map(status => ({ ...status,
        status_target_id: upper(status.target), status_caster_id: upper(status.caster) }));
    const transportChanged = [...verdict.statusesAdded, ...verdict.statusesRemoved].some(status => isPilotingStatus(upper(status.status_item_id)));
    const heldRows = transportChanged ? [...roster.values(), ...Array.isArray(encounter?.encounter_inventories_arr) ? encounter.encounter_inventories_arr as Row[] : []] : [];
    const peers = [...addedPeers, ...pilotedStatusPeers(heldRows)
        .filter(status => !verdict.statusesRemoved.some(removed => removed.status_id
            ? upper(removed.status_id) === statusId(status)
            : upper(removed.target) === upper(readItemField(status, 'status_target_id')) && upper(removed.status_item_id) === statusItemId(status)))];
    for (const [id, member] of roster) {
        const added = verdict.statusesAdded.filter(s => [upper(s.target), upper(s.caster)].includes(id));
        const removed = verdict.statusesRemoved.filter(s => upper(s.target) === id
            || statusRows(member, 'caster').some(row => s.status_id
                ? statusId(row) === upper(s.status_id)
                : statusItemId(row) === upper(s.status_item_id) && upper(readItemField(row, 'status_target_id')) === upper(s.target)));
        const stacks = verdict.statusStacks?.filter(s => upper(s.target) === id) ?? [];
        if (!added.length && !removed.length && !stacks.length) continue;
        const before = { target: statusRows(member, 'target'), caster: statusRows(member, 'caster') };
        const ids = new Set([...removed.flatMap(r => [...before.target, ...before.caster].filter(s => r.status_id ? statusId(s) === upper(r.status_id)
            : statusItemId(s) === upper(r.status_item_id) && upper(readItemField(s, 'status_target_id')) === upper(r.target)).map(s => statusId(s))), ...stacks.map(s => statusId(s)),
            ...added.map(s => statusId(s))]);
        const side = (mode: 'target' | 'caster'): Row[] => {
            const changes: Row[] = before[mode].filter(s => stacks.some(stack => upper(stack.status_id) === statusId(s)))
                .map(s => ({ ...s, status_stack: stacks.find(stack => upper(stack.status_id) === statusId(s))!.status_stack }));
            for (const s of added) if (upper(mode === 'target' ? s.target : s.caster) === id) changes.push({ ...s, ...typeof s.ends === 'string' ? statusEndFields(s.ends) : {},
                status_target_id: upper(s.target), status_caster_id: upper(s.caster), status_arr: definition(upper(s.status_item_id)) });
            return changes;
        };
        const catalog = member.catalog as Row | undefined;
        const scopedDefinition = (key: string): Row | null => fullDefinition(catalog?.[upper(key)] as Row | null, 'any') ?? definition(key);
        const state = { ids: [...ids], target: side('target'), caster: side('caster') };
        const changed = [...added, ...removed, ...stacks];
        const transportOnly = changed.every(status => isPilotingStatus(upper(status.status_item_id)));
        let failure: string | null;
        if (transportOnly) {
            const after = mergeAttackStatuses(member, state);
            if (added.some(status => upper(status.target) === id) || removed.some(status => upper(status.target) === id) || stacks.length)
                failure = projectPilotedAbilityDiff(member, after, heldRows, peers, scopedDefinition);
            else { member.statuses = after; failure = null; }
        } else failure = projectAttackStatuses(member, state, scopedDefinition, roster, scene);
        member._status_projection_failure = failure;
        if (failure) {
            if (transportOnly) member._piloted_ability_refusal = failure;
            // Saved rows still land. Keep the failed derived projection explicit
            // without suppressing this member's independently decided writes.
            Object.defineProperty(member, '_action_status_projection_failure', { value: failure, configurable: true, writable: true });
            if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') window.dispatchEvent(new CustomEvent('ft:status-projection-failed', {
                detail: { reason: failure, memberId: id },
            }));
            failures.push(String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id')));
        }
    }
    return failures;
}
